import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { ROUTE } from '~/app/routes';
import { Details } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardRepository } from '~/repositories/CardRepository';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';

import { CardProps } from './Card';

interface Params {
  id: string;
}

const cardQueryKey = (id: string) => ['card', id] as const;

export const PageCardApiResponse: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const { card: outletCard } = useOutletContext<CardProps>();

  const { data: card, error: cardError } = useQuery({
    queryKey: cardQueryKey(id!),
    queryFn: () => CardRepository.get(id!),
    enabled: !!id && !outletCard,
    initialData: outletCard,
  });

  useEffect(() => {
    if (cardError) {
      navigate(ROUTE.CARDS);
      addDangerNotification({
        content: (cardError as any).message || 'Failed to load card',
      });
    }
  }, [cardError, navigate, addDangerNotification]);

  const formattedCard = useMemo(() => (card ? (card as unknown as Record<string, unknown>) : undefined), [card]);

  if (!card) {
    return null;
  }

  return (
    <Inner pt={24} px={0} pb={0}>
      <Details
        label="API Response"
        formattedData={formattedCard}
        rawData={card as unknown as Record<string, unknown> | undefined}
      />
    </Inner>
  );
};
